// ============================================================
// The option bar's panels: volume, material, surface, arena, clipboard
// ============================================================
// Every control writes `build`; nothing here owns state, which is what
// lets the terrain editor be a second view of the same state.

import clsx from '../../utils/clsx';
import common from '../../styles/common.module.css';
import styles from '../../styles/Battlefield.module.css';
import SliderInput from '../SliderInput';
import MapIcon from '../MapIcon';
import { MaterialChips, SurfaceChips } from './controls';
import { ArenaShapeField, ArenaSliders } from './ArenaControls';
import { OptionRow } from './editorParts';

/** How the scene is lit (engine/battlefield/lighting): set on the scene, read by Stealth on the display. */
const LIGHTING = [
  { value: 'daylight', label: 'Daylight', hint: 'Everywhere is lit' },
  { value: 'lit-areas', label: 'Lit areas', hint: 'Lit where ground is painted with a lit surface, dark elsewhere' },
  { value: 'darkness', label: 'Darkness', hint: 'Nowhere is lit' },
];

/** One of the small text buttons along the bar. */
function SmallButton({ testId, onClick, title, disabled, children }) {
  return (
    <button
      type="button"
      className={common.btnSmall}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/** "Put the built-in entries back. Your own are kept." */
function RestoreDefaults({ testId, onClick }) {
  return (
    <SmallButton testId={testId} onClick={onClick} title="Put the built-in entries back. Your own are kept.">
      ↺ Restore defaults
    </SmallButton>
  );
}

/**
 * How tall a drag builds, and where it sits. The storey is only worth
 * showing when stacking is OFF: with it on, it is a floor the auto-stack
 * overrides, which is a rule nobody can hold in their head while building.
 */
export function VolumePanel({ build }) {
  return (
    <>
      <SliderInput
        label={build.tool === 'dig' ? 'Depth' : 'Height'}
        name="Layers"
        data-testid="build-layers"
        min={1}
        max={40}
        value={build.layers}
        onChange={(value) => build.setLayers(value || 1)}
        unit="cubes"
        title="How many cubes tall this drag builds"
      />
      <label className={clsx(common.label, common.checkRow)} style={{ marginBottom: 0 }}>
        <input
          type="checkbox"
          aria-label="Stack on top"
          data-testid="build-autostack"
          checked={build.autoStack}
          onChange={(e) => build.setAutoStack(e.target.checked)}
        />
        Stack on top
      </label>
      {!build.autoStack && (
        <SliderInput
          label="Storey"
          name="Build level"
          data-testid="build-level"
          min={-20}
          max={40}
          value={build.level}
          onChange={(value) => build.setLevel(value ?? 0)}
          title="Which storey to build on. 0 is the ground, −1 is below it"
        />
      )}
    </>
  );
}

export function MaterialPanel({ build }) {
  return (
    <>
      <MaterialChips build={build} prefix="material-" withTitles />
      <RestoreDefaults testId="restore-materials" onClick={build.restoreMaterials} />
    </>
  );
}

/**
 * The ground types, and a name for the area - a named place IS ground:
 * "the Chapel steps" is somewhere you stand, not something you hide behind.
 */
export function SurfacePanel({ build }) {
  return (
    <>
      <SurfaceChips build={build} prefix="zone-" newTestId="surface-new" withTitles />
      <label className={styles.buildField}>
        Name this area
        <input
          className={common.input}
          aria-label="Terrain label"
          value={build.zoneLabel}
          placeholder="The Chapel steps"
          onChange={(e) => build.setZoneLabel(e.target.value)}
        />
      </label>
      <RestoreDefaults testId="restore-surfaces" onClick={build.restoreSurfaces} />
    </>
  );
}

/** Draw a custom ring corner by corner, or finish and cancel one being drawn. */
function ArenaDraw({ build }) {
  const draft = build.arenaDraft;
  if (!draft) {
    return (
      <SmallButton
        testId="arena-draw"
        onClick={build.startArenaDraw}
        title="Click the map corner by corner; click the first corner again or Finish to close the ring"
      >
        ✎ Draw custom
      </SmallButton>
    );
  }
  return (
    <>
      <span className={styles.buildHint} data-testid="arena-draw-hint">
        {draft.length} corner{draft.length === 1 ? '' : 's'} - click the map; right-click undoes
      </span>
      <SmallButton testId="arena-draw-finish" disabled={draft.length < 3} onClick={build.finishArenaDraw}>
        Finish
      </SmallButton>
      <SmallButton testId="arena-draw-cancel" onClick={build.cancelArenaDraw}>
        Cancel
      </SmallButton>
    </>
  );
}

/**
 * The floor everything stands on. CLEAR LIVES WITH THE ARENA, not with
 * Volume: it wipes solids, zones and cubes alike - the floor being swept,
 * not a setting about how tall a drag is (v8.0.0).
 */
export function ArenaPanel({ build, boundary, faceCount }) {
  return (
    <>
      <ArenaShapeField build={build} boundary={boundary} />
      <ArenaSliders build={build} boundary={boundary} />
      <ArenaDraw build={build} />
      <OptionRow
        label="Lighting"
        options={LIGHTING}
        value={build.lighting}
        testPrefix="lighting-"
        onPick={build.setLighting}
      />
      <SmallButton testId="build-clear" onClick={build.clearTerrain}>
        Clear terrain
      </SmallButton>
      {faceCount > 1200 && (
        <span className={styles.importWarn} data-testid="build-budget">
          {faceCount} polygons - getting heavy
        </span>
      )}
    </>
  );
}

/** A clipboard control: a plain brush-styled button. */
function ClipButton({ testId, onClick, title, children }) {
  return (
    <button type="button" className={styles.brush} data-testid={testId} onClick={onClick} title={title}>
      {children}
    </button>
  );
}

/** What can be done with a stamp in hand: flip it, turn it, drop it. */
function HeldStamp({ build, stamp }) {
  return (
    <>
      <span className={styles.buildHint} data-testid="clip-held">
        {stamp.width}×{stamp.depth} held - CLICK to stamp, drag to select again
      </span>
      <ClipButton
        testId="clip-mirror-x"
        onClick={() => build.mirrorClipboard('x')}
        title="Flip it left to right. Press again to put it back."
      >
        ⇋ X
      </ClipButton>
      <ClipButton testId="clip-mirror-z" onClick={() => build.mirrorClipboard('z')} title="Flip it front to back">
        ⇵ Z
      </ClipButton>
      <ClipButton testId="clip-rotate" onClick={build.rotateClipboard} title="Turn it a quarter turn">
        ↻
      </ClipButton>
      <ClipButton testId="clip-clear" onClick={build.clearClipboard}>
        ✕ Drop
      </ClipButton>
    </>
  );
}

/**
 * Copy a region, turn it about, and stamp it again - the Select tool's own
 * controls rather than something sitting under everything.
 */
export function Clipboard({ build }) {
  const { region, stamp } = build;
  return (
    <div className={styles.brushRow} role="group" aria-label="Clipboard">
      {!region && !stamp && <span className={styles.buildHint}>Drag out a region to copy</span>}
      {region && (
        <ClipButton testId="clip-copy" onClick={build.copySelection}>
          <MapIcon name="select" /> Copy
        </ClipButton>
      )}
      {stamp && <HeldStamp build={build} stamp={stamp} />}
    </div>
  );
}
